import React, { useState } from 'react';
import { SecondaryLink } from '../primitives/buttons';
import { GoogleGlyph, D20Icon } from '../primitives/icons';

export const SignInView = ({ theme, density, palettes, cardStyle, cardFinish, onSignIn, onEnterDemoCampaign }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailFocus, setEmailFocus] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [signInHover, setSignInHover] = useState(false);
  const [googleHover, setGoogleHover] = useState(false);
  const [playHover, setPlayHover] = useState(false);
  const [dmHover, setDmHover] = useState(false);
  const [openDemoHover, setOpenDemoHover] = useState(false);

  // Use a single palette slot across both cards so the page reads
  // monochromatic rather than polychromatic. The theme accent already
  // does the identity work.
  const palette = palettes[0];

  // Solid panel surface — sourced from the theme's bgCardSolid. Gains a
  // subtle palette-tinted border and a tight palette glow (small spread,
  // close to the edge) so the panel reads as warm without the soft halo
  // looking blurry. Matches the visual warmth of Ornate's campaign hero
  // strip without overdoing it.
  const panelStyle = {
    background: c.bgCardSolid,
    border: `1px solid ${palette.color}40`,
    borderRadius: 10,
    boxShadow: `0 8px 32px rgba(0,0,0,0.45), 0 0 18px -6px ${palette.color}66`,
    position: 'relative',
    overflow: 'hidden',
  };

  // Input — rectangular (6px radius), quiet fill, palette border on focus.
  // No pill shape, no heavy chrome.
  const inputStyle = (focused) => ({
    width: '100%',
    background: 'rgba(0,0,0,0.28)',
    border: `1px solid ${focused ? palette.color : 'rgba(255,255,255,0.10)'}`,
    borderRadius: 6,
    padding: '11px 14px',
    color: c.textPrimary,
    fontFamily: t.fontFamily,
    fontSize: '0.94rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: e.transition || 'border-color 150ms ease',
  });

  // Field label — sentence-case, small, restrained.
  const fieldLabelStyle = {
    fontFamily: t.fontFamily,
    fontSize: '0.78rem',
    fontWeight: 600,
    color: c.textSecondary,
    marginBottom: 4,
    letterSpacing: '0.02em',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{
        width: '100%', maxWidth: 460,
        display: 'flex', flexDirection: 'column', gap: 20,
      }}>
        {/* Wordmark — Survival Tracker with crossed swords on each side, matching the
            old homepage's signature treatment. Serif heading in palette
            accent (theme accent here since the sign-in page isn't tied to a
            campaign palette). The swords aren't decoration — they are part
            of the wordmark. */}
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <h1 style={{
            margin: 0,
            fontFamily: t.headingFamily,
            fontSize: '1.7rem',
            fontWeight: 600,
            color: c.accentLight,
            letterSpacing: '0.04em',
            textShadow: e.headingTextShadow,
            display: 'inline-flex', alignItems: 'center', gap: 12,
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: '1.3rem' }}>⚔️</span>
            <span>Survival Tracker</span>
            <span style={{ fontSize: '1.3rem' }}>⚔️</span>
          </h1>
          <div style={{
            color: c.textSecondary,
            fontSize: '0.92rem',
            fontStyle: 'italic',
            fontFamily: t.fontFamily,
            marginTop: 6,
          }}>Sign in to continue the adventure.</div>
        </div>

        {/* Sign-in panel — solid surface with subtle Ornate flourishes:
            palette-tinted border + soft palette glow shadow (from
            panelStyle), plus a top-edge palette accent line and a
            diagonal palette gradient overlay across the panel surface. */}
        <div style={{
          ...panelStyle,
          padding: '24px 24px 22px',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {/* Top-edge palette accent — matches CampaignHeroStrip */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2,
            background: `linear-gradient(90deg, ${palette.color} 0%, ${palette.light}88 50%, transparent 100%)`,
            opacity: 0.7,
          }} />
          {/* Diagonal palette gradient overlay — gentle wash across the panel */}
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(135deg, ${palette.color}1f 0%, transparent 60%)`,
            pointerEvents: 'none',
          }} />
          {/* All sign-in content sits above the overlays via stacking context */}
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={fieldLabelStyle}>Email or username</div>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={ev => setEmail(ev.target.value)}
              onFocus={() => setEmailFocus(true)}
              onBlur={() => setEmailFocus(false)}
              style={inputStyle(emailFocus)}
            />
          </div>
          <div>
            <div style={fieldLabelStyle}>Password</div>
            <input
              type="password"
              placeholder="•••••••••"
              value={password}
              onChange={ev => setPassword(ev.target.value)}
              onFocus={() => setPasswordFocus(true)}
              onBlur={() => setPasswordFocus(false)}
              style={inputStyle(passwordFocus)}
            />
          </div>

          {/* Primary action — solid palette button, no gradient, 6px radius */}
          <button
            onClick={onSignIn}
            onMouseEnter={() => setSignInHover(true)}
            onMouseLeave={() => setSignInHover(false)}
            style={{
              background: signInHover ? palette.light : palette.color,
              color: c.textOnAccent,
              border: 'none',
              borderRadius: 6,
              padding: '11px 16px',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.94rem',
              cursor: 'pointer',
              marginTop: 6,
              transition: 'background 150ms ease',
            }}
          >Sign in</button>

          {/* Secondary actions: forgot/create as inline links */}
          <div style={{
            display: 'flex', gap: 14, justifyContent: 'space-between',
            marginTop: 2, flexWrap: 'wrap',
            fontFamily: t.fontFamily,
            fontSize: '0.82rem',
          }}>
            <SecondaryLink theme={theme}>Forgot password?</SecondaryLink>
            <SecondaryLink theme={theme}>Create account</SecondaryLink>
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, margin: '6px 0 2px',
          }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
            <span style={{
              fontFamily: t.fontFamily,
              fontSize: '0.7rem',
              color: c.textMuted,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
            }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Google — ghost button vocabulary (matches V2's "Set reminder") */}
          <button
            onMouseEnter={() => setGoogleHover(true)}
            onMouseLeave={() => setGoogleHover(false)}
            style={{
              background: googleHover ? 'rgba(255,255,255,0.04)' : 'transparent',
              border: `1px solid rgba(255,255,255,0.12)`,
              borderRadius: 6,
              padding: '10px 16px',
              color: c.textPrimary,
              fontFamily: t.fontFamily,
              fontSize: '0.92rem',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: e.transition,
              whiteSpace: 'nowrap',
            }}
          >
            <GoogleGlyph /> Continue with Google
          </button>
          </div>{/* end inner content wrapper */}
        </div>

        {/* Demo panel — same structure as the home page's DemoCampaignCard:
            D20 icon on the left, content stack on the right. The buttons
            replace the home page's single "Open demo →" with three concrete
            entry points (play as Li'ka, DM view, open demo campaign). */}
        <div style={{
          ...panelStyle,
          padding: '20px 22px',
          position: 'relative',
        }}>
          {/* Subtle palette tint at the top so the demo card has a touch of
              warmth without overpowering the page's monochromatic feel */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 80,
            background: `linear-gradient(180deg, ${palette.color}1f 0%, transparent 100%)`,
            pointerEvents: 'none',
            borderRadius: '10px 10px 0 0',
          }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <D20Icon size={40} />
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '0.62rem',
                letterSpacing: '0.22em',
                color: palette.light,
                textTransform: 'uppercase',
                marginBottom: 4,
                fontWeight: 700,
                fontFamily: t.fontFamily,
              }}>New Here?</div>
              <div style={{
                fontFamily: t.headingFamily || 'Georgia, serif',
                fontSize: '1.15rem',
                color: c.accentLight,
                marginBottom: 6,
                fontWeight: 600,
                textShadow: e.headingTextShadow,
              }}>Try the Demo Campaign</div>
              <div style={{
                color: c.textSecondary,
                fontSize: '0.82rem',
                lineHeight: 1.5,
                marginBottom: 12,
                fontFamily: t.fontFamily,
              }}>
                Four pre-built characters. No signup.
              </div>

              {/* Demo entry points: primary play, ghost DM view, tertiary
                  open-demo link beneath. */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: 10,
              }}>
                <button
                  onClick={() => onEnterDemoCampaign?.({ role: 'player', charId: 'demo-lika' })}
                  onMouseEnter={() => setPlayHover(true)}
                  onMouseLeave={() => setPlayHover(false)}
                  style={{
                    background: playHover ? palette.light : palette.color,
                    color: c.textOnAccent,
                    border: 'none',
                    borderRadius: 6,
                    padding: '9px 14px',
                    fontFamily: t.fontFamily,
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'background 150ms ease',
                  }}
                >▸ Play as Li'ka</button>
                <button
                  onClick={() => onEnterDemoCampaign?.({ role: 'dm' })}
                  onMouseEnter={() => setDmHover(true)}
                  onMouseLeave={() => setDmHover(false)}
                  style={{
                    background: dmHover ? 'rgba(255,255,255,0.04)' : 'transparent',
                    color: c.textPrimary,
                    border: `1px solid rgba(255,255,255,0.12)`,
                    borderRadius: 6,
                    padding: '9px 14px',
                    fontFamily: t.fontFamily,
                    fontWeight: 500,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'all 120ms ease',
                  }}
                >👑 DM view</button>
              </div>

              <div style={{
                display: 'flex', justifyContent: 'flex-start',
                marginTop: 10,
              }}>
                <button
                  onMouseEnter={() => setOpenDemoHover(true)}
                  onMouseLeave={() => setOpenDemoHover(false)}
                  onClick={onSignIn}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 0',
                    color: openDemoHover ? palette.light : c.textMuted,
                    fontFamily: t.fontFamily,
                    fontSize: '0.78rem',
                    fontStyle: 'italic',
                    cursor: 'pointer',
                    textDecoration: openDemoHover ? 'underline' : 'none',
                    textUnderlineOffset: 4,
                    textDecorationColor: palette.color,
                    transition: 'color 150ms ease',
                  }}
                >Or just open the demo campaign →</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
